import { useAsync } from '@nxsys/hooks/useAsync';
import {
  approveExpenseApi,
  approvePayrollApi,
  confirmPaymentApi,
  generatePaymentFileApi,
  getAgencyStatisticsApi,
  getAgencyUploadedFileListApi,
  getApproverListApi,
  getCompanyPaymentGroupingApi,
  getGroupingPayrollByPeriodApi,
  getStatisticsClientApprovalRejectApi,
  getStatisticsCommunicationApi,
  getStatisticsEmployeeDashboardApi,
  getStatisticsEmployeeIrelandApi,
  getStatisticsComplianceApi,
  getSafeRecErrorListApi,
  getVeriPayeErrorListApi,
  resendSafeRecApi,
  resendVeriPayeApi,
  getStatisticsEmployeeSubcontractorApi,
  getStatisticsInvoicingApi,
  getStatisticsMiscellaneousApi,
  getStatisticsOnHoldApi,
  getStatisticsPaymentsApi,
  getStatisticsPayrollImportApi,
  getStatisticsPayrollImportIrelandApi,
  getStatisticsPensionContributionApi,
  getStatisticsPensionSubcontractorsApi,
  getStatisticsRevenueSubmissionApi,
  getStatisticsOthersApi,
  getStatisticsRTISubmissionApi,
  getStatisticsTimesheetsApi,
  getPayBreakdownApi,
  getPayrollPeriodsApi,
  getPensionContributionApi,
  getTimesheetBreakdownApi,
  getUpcomingPaymentApi,
  markAsSubmittedCIS300Api,
  rejectExpenseApi,
  rejectPayrollApi,
  requestApproverApi,
  submitCIS300Api,
  submitCISDeductionsSufferedApi,
  submitEPSApi,
  submitFPSApi,
  submitLeaverFPSApi,
  uploadAgencyFileApi,
} from '@nxsys/services/api/dashboard';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import {
  ApproveExpenseParams,
  ApproveExpenseResponse,
  ApprovePayrollParams,
  ApprovePayrollResponse,
  ConfirmPaymentParams,
  ConfirmPaymentResponse,
  GeneratePaymentFileParams,
  GeneratePaymentFileResponse,
  GetAgencyStatisticsParams,
  GetAgencyStatisticsResponse,
  GetAgencyUploadedFileListParams,
  GetAgencyUploadedFileListResponse,
  GetCompanyPaymentGroupingParams,
  GetCompanyPaymentGroupingResponse,
  GetGroupingPayrollByPeriodResponse,
  GetListApproverParams,
  GetListApproverResponse,
  GetStatisticsClientApprovalRejectResponse,
  GetStatisticsCommunicationResponse,
  GetStatisticsEmployeeDashboardParams,
  GetStatisticsEmployeeDashboardResponse,
  GetStatisticsComplianceResponse,
  SafeRecErrorListResponse,
  VeriPayeErrorListResponse,
  ComplianceErrorListParams,
  SafeRecResendParams,
  VeriPayeResendParams,
  GetStatisticsEmployeeIrelandResponse,
  GetStatisticsEmployeeSubcontractorResponse,
  GetStatisticsInvoicingResponse,
  GetStatisticsMiscellaneousResponse,
  GetStatisticsOnHoldResponse,
  GetStatisticsPaymentsResponse,
  GetStatisticsPayrollImportResponse,
  GetStatisticsPensionContributionParams,
  GetStatisticsPensionContributionResponse,
  GetStatisticsPensionSubcontractorsResponse,
  GetPensionContributionResponse,
  GetStatisticsRevenueSubmissionResponse,
  GetStatisticsOthersResponse,
  GetStatisticsRTISubmissionResponse,
  GetStatisticsTimesheetsResponse,
  MarkAsSubmittedCIS300Params,
  MarkAsSubmittedCIS300Response,
  RejectExpenseParams,
  RejectExpenseResponse,
  RejectPayrollParams,
  RejectPayrollResponse,
  RequestApproverParams,
  RequestApproverResponse,
  SubmitCIS300Params,
  SubmitCIS300Response,
  SubmitCISDeductionsSufferedParams,
  SubmitCISDeductionsSufferedResponse,
  SubmitEPSParams,
  SubmitEPSResponse,
  SubmitFPSParams,
  SubmitFPSResponse,
  SubmitLeaverFPSParams,
  SubmitLeaverFPSResponse,
  UploadAgencyFileParams,
  UploadAgencyFileResponse,
} from '@nxsys/types/api/dashboard';
import {
  PayBreakdownParams,
  PayBreakdownResponse,
  PayrollPeriodsResponse,
} from '@nxsys/types/api/payBreakdown';
import { UpcomingPaymentResponse } from '@nxsys/types/api/upcomingPayment';
import { TimesheetBreakdownResponse } from '@nxsys/types/api/timesheetBreakdown';
import { showMessage } from '@nxsys/utils/message';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

export const useSubmitCIS300 = () => {
  const { value, loading, error, run } = useAsync<
    SubmitCIS300Response,
    SubmitCIS300Params
  >(submitCIS300Api);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useMarkAsSubmittedCIS300 = () => {
  const { value, loading, error, run } = useAsync<
    MarkAsSubmittedCIS300Response,
    MarkAsSubmittedCIS300Params
  >(markAsSubmittedCIS300Api);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSubmitCISDeductionsSuffered = () => {
  const { value, loading, error, run } = useAsync<
    SubmitCISDeductionsSufferedResponse,
    SubmitCISDeductionsSufferedParams & { companyId: string }
  >(({ companyId, ...params }) =>
    submitCISDeductionsSufferedApi(companyId, params),
  );

  const _run = async (
    companyId: string,
    params: SubmitCISDeductionsSufferedParams,
  ) => {
    return await run({ ...params, companyId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useSubmitEPS = () => {
  const { value, loading, error, run } = useAsync<
    SubmitEPSResponse,
    SubmitEPSParams
  >(submitEPSApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSubmitFPS = () => {
  const { value, loading, error, run } = useAsync<
    SubmitFPSResponse,
    SubmitFPSParams
  >(submitFPSApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useSubmitLeaverFPS = () => {
  const { value, loading, error, run } = useAsync<
    SubmitLeaverFPSResponse,
    SubmitLeaverFPSParams
  >(submitLeaverFPSApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsTimesheets = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsTimesheetsResponse,
    { paymentModels?: string[] } | undefined
  >(getStatisticsTimesheetsApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useApproveExpense = () => {
  const { value, loading, error, run } = useAsync<
    ApproveExpenseResponse,
    ApproveExpenseParams
  >(approveExpenseApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useRejectExpense = () => {
  const { value, loading, error, run } = useAsync<
    RejectExpenseResponse,
    RejectExpenseParams
  >(rejectExpenseApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsInvoicing = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsInvoicingResponse,
    void
  >(getStatisticsInvoicingApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsOnHold = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsOnHoldResponse,
    void
  >(getStatisticsOnHoldApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsPayments = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsPaymentsResponse,
    { paymentModels?: string[] } | undefined
  >(getStatisticsPaymentsApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGeneratePaymentFile = () => {
  const { value, loading, error, run } = useAsync<
    GeneratePaymentFileResponse,
    GeneratePaymentFileParams & { companyId: string, queryParams: { isBureau: boolean, isIreland: boolean } }
  >(({ companyId, queryParams, ...params }) => generatePaymentFileApi(companyId, params, queryParams));
  const { isBureau, payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const _run = async (companyId: string, params: GeneratePaymentFileParams) => {
    return await run({ ...params, companyId, queryParams: { isBureau, isIreland } });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useConfirmPayment = () => {
  const { value, loading, error, run } = useAsync<
    ConfirmPaymentResponse,
    ConfirmPaymentParams
  >(confirmPaymentApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsPensionContribution = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsPensionContributionResponse,
    GetStatisticsPensionContributionParams
  >(getStatisticsPensionContributionApi);

  const _run = async (params: GetStatisticsPensionContributionParams = {}) => {
    return await run(params);
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetStatisticsPensionSubcontractors = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsPensionSubcontractorsResponse,
    void
  >(getStatisticsPensionSubcontractorsApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsEmployeeSubcontractor = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsEmployeeSubcontractorResponse,
    void
  >(getStatisticsEmployeeSubcontractorApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsCompliance = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsComplianceResponse,
    void
  >(() => getStatisticsComplianceApi());

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsMiscellaneous = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsMiscellaneousResponse,
    void
  >(getStatisticsMiscellaneousApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetCompanyPaymentGrouping = () => {
  const { value, loading, error, run } = useAsync<
    GetCompanyPaymentGroupingResponse,
    GetCompanyPaymentGroupingParams
  >(getCompanyPaymentGroupingApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsPayrollImport = () => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const { value, loading, error, run } = useAsync<
    GetStatisticsPayrollImportResponse,
    { paymentModels?: string[] } | undefined
  >(
    isIreland
      ? getStatisticsPayrollImportIrelandApi
      : getStatisticsPayrollImportApi,
  );

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsClientApprovalReject = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsClientApprovalRejectResponse,
    { paymentModels?: string[] } | undefined
  >(getStatisticsClientApprovalRejectApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetListApprover = () => {
  const { value, loading, error, run } = useAsync<
    GetListApproverResponse,
    GetListApproverParams & { payrollId: string }
  >(({ payrollId, ...params }) => getApproverListApi(payrollId, params));

  const _run = async (payrollId: string, params: GetListApproverParams) => {
    return await run({ ...params, payrollId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useRequestApprover = () => {
  const { value, loading, error, run } = useAsync<
    RequestApproverResponse,
    RequestApproverParams & { payrollId: string }
  >(({ payrollId, ...params }) => requestApproverApi(payrollId, params));

  const _run = async (payrollId: string, params: RequestApproverParams) => {
    return await run({ ...params, payrollId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useApprovePayroll = () => {
  const { value, loading, error, run } = useAsync<
    ApprovePayrollResponse,
    ApprovePayrollParams
  >(approvePayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useRejectPayroll = () => {
  const { value, loading, error, run } = useAsync<
    RejectPayrollResponse,
    RejectPayrollParams
  >(rejectPayrollApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsRTISubmission = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsRTISubmissionResponse,
    void
  >(getStatisticsRTISubmissionApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsOthers = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsOthersResponse,
    void
  >(getStatisticsOthersApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsCommunication = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsCommunicationResponse,
    void
  >(getStatisticsCommunicationApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetGroupingPayrollByPeriod = () => {
  const { value, loading, error, run } = useAsync<
    GetGroupingPayrollByPeriodResponse,
    void
  >(getGroupingPayrollByPeriodApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsRevenueSubmission = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsRevenueSubmissionResponse,
    void
  >(getStatisticsRevenueSubmissionApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsEmployeeIreland = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsEmployeeIrelandResponse,
    void
  >(getStatisticsEmployeeIrelandApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useUploadAgencyFile = () => {
  const { value, loading, error, run } = useAsync<
    UploadAgencyFileResponse,
    UploadAgencyFileParams & { agencyId: string }
  >(({ agencyId, ...params }) => uploadAgencyFileApi(agencyId, params));

  const _run = async (agencyId: string, params: UploadAgencyFileParams) => {
    return await run({ ...params, agencyId });
  };

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run: _run,
  };
};

export const useGetAgencyUploadedFileList = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyUploadedFileListResponse,
    GetAgencyUploadedFileListParams
  >(getAgencyUploadedFileListApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetAgencyStatistics = () => {
  const { value, loading, error, run } = useAsync<
    GetAgencyStatisticsResponse,
    GetAgencyStatisticsParams
  >(getAgencyStatisticsApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetStatisticsEmployeeDashboard = () => {
  const { value, loading, error, run } = useAsync<
    GetStatisticsEmployeeDashboardResponse,
    GetStatisticsEmployeeDashboardParams
  >(getStatisticsEmployeeDashboardApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetUpcomingPayment = () => {
  const { value, loading, error, run } = useAsync<
    UpcomingPaymentResponse,
    string
  >(getUpcomingPaymentApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetPayBreakdown = () => {
  const { value, loading, error, run } = useAsync<
    PayBreakdownResponse,
    PayBreakdownParams | undefined
  >(getPayBreakdownApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetTimesheetBreakdown = () => {
  const { value, loading, error, run } = useAsync<
    TimesheetBreakdownResponse,
    void
  >(getTimesheetBreakdownApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetPayrollPeriods = () => {
  const { value, loading, error, run } = useAsync<
    PayrollPeriodsResponse,
    void
  >(getPayrollPeriodsApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetPensionContribution = () => {
  const { value, loading, error, run } = useAsync<
    GetPensionContributionResponse,
    string
  >(getPensionContributionApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return {
    value,
    loading,
    error,
    run,
  };
};

export const useGetSafeRecErrorList = () => {
  const { value, loading, error, run } = useAsync<
    SafeRecErrorListResponse,
    ComplianceErrorListParams
  >(getSafeRecErrorListApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useGetVeriPayeErrorList = () => {
  const { value, loading, error, run } = useAsync<
    VeriPayeErrorListResponse,
    ComplianceErrorListParams
  >(getVeriPayeErrorListApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useResendSafeRec = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    SafeRecResendParams
  >(resendSafeRecApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run };
};

export const useResendVeriPaye = () => {
  const { value, loading, error, run } = useAsync<
    { success: boolean },
    VeriPayeResendParams
  >(resendVeriPayeApi);

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  return { value, loading, error, run };
};
